import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaVsConvencional = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre diferenças de limpeza e gostaria de um orçamento.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/limpeza-vs-convencional'].title}
        description={seoData['/blog/limpeza-vs-convencional'].description}
        canonical={seoData['/blog/limpeza-vs-convencional'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Limpeza Pós-Obra vs Limpeza Convencional: Qual a Diferença?
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>7 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Muitas pessoas acreditam que uma <strong>limpeza convencional</strong> é suficiente após uma obra ou reforma. Na prática, a <strong>limpeza pós-obra</strong> é completamente diferente e exige técnicas, produtos e equipamentos especializados.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Principais Diferenças
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-8">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Aspecto</th>
                    <th className="p-4 text-left">Limpeza Convencional</th>
                    <th className="p-4 text-left">Limpeza Pós-Obra</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Tipo de Sujeira</td>
                    <td className="p-4">Pó, manchas leves, sujeira do dia a dia</td>
                    <td className="p-4">Cimento, argamassa, rejunte, tinta, cola, entulho</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Produtos</td>
                    <td className="p-4">Detergentes comuns, multiuso</td>
                    <td className="p-4">Removedores ácidos, desengraxantes industriais, produtos específicos</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Equipamentos</td>
                    <td className="p-4">Rodo, pano, vassoura, aspirador doméstico</td>
                    <td className="p-4">Lavadora de piso, aspirador industrial, raspadores, politriz</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Tempo</td>
                    <td className="p-4">2-4 horas (apartamento)</td>
                    <td className="p-4">6-12 horas (apartamento)</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">Mão de Obra</td>
                    <td className="p-4">Diarista, faxineira</td>
                    <td className="p-4">Equipe especializada treinada</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              O Que a Limpeza Convencional NÃO Remove
            </h2>

            <Card className="bg-red-50 border-2 border-red-200 mb-6">
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Resíduos de cimento e argamassa</strong> grudados no piso</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Rejunte em excesso</strong> nos azulejos</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Manchas de tinta</strong> em vidros e pisos</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Cola de proteções</strong> em esquadrias</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Pó fino</strong> acumulado em cantos e frestas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Resíduos em trilhos</strong> de janelas e portas</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              O Que a Limpeza Pós-Obra Profissional Oferece
            </h2>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Remoção total</strong> de resíduos de construção</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Produtos especializados</strong> para cada tipo de sujeira</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Equipamentos industriais</strong> (lavadoras, aspiradores profissionais)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Limpeza profunda</strong> de vidros, esquadrias e metais</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Polimento e acabamento</strong> de superfícies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Garantia de resultado</strong> e obra entregue pronta</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Quando Cada Tipo de Limpeza é Indicado?
            </h2>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              Limpeza Convencional é Indicada Para:
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Manutenção semanal ou mensal</li>
              <li>Limpeza de rotina em casa ou escritório</li>
              <li>Após eventos ou festas</li>
              <li>Limpeza pré-mudança (sem obra)</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              Limpeza Pós-Obra é Indicada Para:
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Após construção ou reforma completa</li>
              <li>Entrega de imóvel novo (construtoras)</li>
              <li>Preparação para inauguração comercial</li>
              <li>Após instalação de pisos ou revestimentos</li>
              <li>Depois de pinturas ou reboco</li>
              <li>Preparação de galpão industrial para operação</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Posso Fazer Limpeza Convencional Após a Obra?
            </h2>
            <p className="text-gray-700 mb-6">
              Não é recomendado. A limpeza convencional não tem capacidade de remover resíduos de construção e pode até danificar superfícies ao tentar remover manchas difíceis com produtos inadequados. O resultado será um imóvel com aparência de "meio limpo", com manchas, resíduos grudados e acabamento ruim.
            </p>

            <Card className="bg-yellow-50 border-2 border-yellow-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">⚠️ Atenção</h3>
                <p className="text-gray-700">
                  Tentar fazer limpeza pós-obra com produtos convencionais pode manchar permanentemente pisos de porcelanato, mármore e granito. Use sempre produtos e técnicas corretas.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Diferença de Custo
            </h2>
            <p className="text-gray-700 mb-4">
              Embora a limpeza pós-obra seja mais cara, o custo se justifica:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Limpeza convencional (apartamento 2 quartos):</strong> R$ 150 - R$ 300</li>
              <li><strong>Limpeza pós-obra (apartamento 2 quartos):</strong> R$ 800 - R$ 1.500</li>
            </ul>
            <p className="text-gray-700 mb-6">
              A diferença de preço reflete o nível de especialização, equipamentos, produtos e tempo necessários.
            </p>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Precisa de Limpeza Pós-Obra Profissional?</h3>
                <p className="mb-6">
                  A WellTec possui equipe especializada e equipamentos profissionais para entregar seu imóvel impecável
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Orçamento Profissional
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Artigos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/custo-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Quanto Custa Limpeza Pós-Obra?</h4>
                  <p className="text-gray-600 text-sm">Guia completo de preços para 2025</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/limpeza-pos-obra-residencial')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Checklist de Limpeza Pós-Obra</h4>
                  <p className="text-gray-600 text-sm">Tudo que deve ser limpo após uma reforma</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em BH e região</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
                <li><button onClick={handleWhatsAppClick} className="hover:text-green-400">WhatsApp</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <button 
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LimpezaVsConvencional;
